import { useState, useEffect, useCallback, useMemo } from "react";

import {
    Check,
    Link2,
    SkipForward,
    AlertCircle,
    Sparkles,
    ChevronDown,
    ChevronRight,
    Calendar,
    ArrowUp,
    ArrowDown,
    Search,
} from "lucide-react";
import { format } from "date-fns";
import { useDebouncedCallback } from "use-debounce";

import { PartSimilarityErrorBoundary } from "~/components/error-boundary";
import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";
import type { ConfidenceLevel } from "../../parts-similarity/utils/confidence";
import {
    getConfidenceBadgeVariant,
    getConfidenceLabel,
    shouldDeemphasize,
} from "../../parts-similarity/utils/confidence";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "~/components/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { Progress } from "~/components/ui/progress";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Separator } from "~/components/ui/separator";
import { H2, H3, P } from "~/components/ui/typography";
import type { TPart } from "~/database/types";
import { useSuggestPartSimilarities } from "~/hooks/mutations/use-suggest-part-similarities";
import type { PartInteractionWithExtras } from "~/modules.server/part/repository";
import { ATTRIBUTE_LIST } from "../utils/part-types";

// AIDEV-NOTE: One-by-one part review with all matches shown for each part

interface LatestInteractionData {
    sessionDate: string;
    sessionTitle: string;
    attributes?: Array<{ name: string; content: string }>;
}

export interface PartSimilarityMatch {
    newPartInteraction: PartInteractionWithExtras;
    existingPart: TPart & { latestInteraction?: LatestInteractionData };
    confidence: ConfidenceLevel | number;
    reasoning: string;
    differences?: string;
}

interface PartSimilarityReviewProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    sessionId: string;
    onComplete?: (
        acceptedMatches: Array<{ interactionId: string; partId: string }>,
    ) => void;
}

export function PartSimilarityReview({
    isOpen,
    onOpenChange,
    sessionId,
    onComplete,
}: PartSimilarityReviewProps) {
    const suggestMutation = useSuggestPartSimilarities();
    const [currentPartIndex, setCurrentPartIndex] = useState(0);
    const [acceptedMatches, setAcceptedMatches] = useState<
        Array<{ interactionId: string; partId: string }>
    >([]);
    const [suggestions, setSuggestions] = useState<PartSimilarityMatch[]>([]);
    const [selectedMatches, setSelectedMatches] = useState<Map<string, string>>(
        new Map(),
    );
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<
        Array<{
            id: string;
            name: string;
            description?: string;
            latestInteraction?: {
                sessionDate: string;
                sessionTitle: string;
            };
        }>
    >([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSearchPopover, setShowSearchPopover] = useState(false);

    const isLoading = suggestMutation.isPending;

    // Group suggestions by part interaction
    const partGroups = suggestions.reduce(
        (groups, match) => {
            const partId = match.newPartInteraction.id;
            if (!groups[partId]) {
                groups[partId] = {
                    part: match.newPartInteraction,
                    matches: [],
                };
            }
            groups[partId].matches.push(match);
            return groups;
        },
        {} as Record<
            string,
            { part: PartInteractionWithExtras; matches: PartSimilarityMatch[] }
        >,
    );

    const partIds = Object.keys(partGroups);
    const currentPartId = partIds[currentPartIndex];
    const currentPartData = currentPartId ? partGroups[currentPartId] : null;
    const progress =
        partIds.length > 0 ? (currentPartIndex / partIds.length) * 100 : 0;

    // Debounced search handler
    const searchParts = useDebouncedCallback(async (query: string) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        try {
            const response = await fetch(
                `/api/parts/search?q=${encodeURIComponent(query)}&limit=8`,
            );
            if (response.ok) {
                const data = await response.json();
                setSearchResults(data.parts || []);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            console.error("Failed to search parts:", error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, 300);

    // Handle search query changes
    useEffect(() => {
        searchParts(searchQuery);
    }, [searchQuery, searchParts]);

    // Close search popover on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest("[data-search-container]")) {
                setShowSearchPopover(false);
            }
        };

        if (showSearchPopover) {
            document.addEventListener("mousedown", handleClickOutside);
            return () =>
                document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [showSearchPopover]);

    // Load suggestions when dialog opens
    useEffect(() => {
        if (isOpen && suggestions.length === 0 && !suggestMutation.isPending) {
            suggestMutation.mutate(
                { sessionId },
                {
                    onSuccess: (data) => {
                        setSuggestions(data.suggestions || []);
                    },
                    onError: (error) => {
                        console.error("Failed to load suggestions:", error);
                        setSuggestions([]);
                    },
                },
            );
        }
    }, [isOpen, sessionId, suggestions.length]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setCurrentPartIndex(0);
            setAcceptedMatches([]);
            setSuggestions([]);
            setSelectedMatches(new Map());
            setSearchQuery("");
            setSearchResults([]);
            setShowSearchPopover(false);
            suggestMutation.reset();
        }
    }, [isOpen]);

    const handleLinkToMatch = (interactionId: string, partId: string) => {
        // Toggle selection
        const newSelected = new Map(selectedMatches);
        const wasSelected = newSelected.get(interactionId) === partId;

        if (wasSelected) {
            // If already selected, just unselect
            newSelected.delete(interactionId);
        } else {
            // Select this match
            newSelected.set(interactionId, partId);
        }
        setSelectedMatches(newSelected);

        // Add to accepted matches
        const existingMatch = acceptedMatches.find(
            (m) => m.interactionId === interactionId,
        );

        if (existingMatch) {
            // Update existing match
            setAcceptedMatches(
                acceptedMatches.map((m) =>
                    m.interactionId === interactionId
                        ? { interactionId, partId }
                        : m,
                ),
            );
        } else {
            // Add new match
            setAcceptedMatches([...acceptedMatches, { interactionId, partId }]);
        }

        // If we're selecting (not unselecting), advance to next part
        if (!wasSelected) {
            setTimeout(() => {
                handleSkipPart();
            }, 300); // Small delay for visual feedback
        }
    };

    const handleSkipPart = () => {
        // Move to next part or complete
        if (currentPartIndex < partIds.length - 1) {
            setCurrentPartIndex(currentPartIndex + 1);
        } else {
            handleComplete();
        }
    };

    const handleComplete = () => {
        onComplete?.(acceptedMatches);
        onOpenChange(false);
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onOpenChange}
            analyticsId="part-similarity-review"
        >
            <DialogContent className="flex max-h-[85vh] max-w-5xl flex-col overflow-hidden">
                <PartSimilarityErrorBoundary
                    onReset={() => suggestMutation.reset()}
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="size-5" />
                            Part Similarity Suggestions
                        </DialogTitle>
                        <DialogDescription>
                            Review potential matches for each new part from this
                            session
                        </DialogDescription>
                    </DialogHeader>

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-12">
                            <div className="size-8 animate-spin rounded-full border-b-2 border-primary" />
                            <P className="text-muted-foreground">
                                Analyzing parts for similarities...
                            </P>
                        </div>
                    ) : !isLoading && suggestions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-12">
                            <AlertCircle className="size-12 text-muted-foreground" />
                            <P className="text-muted-foreground">
                                No similarity suggestions found for this session
                            </P>
                        </div>
                    ) : currentPartData ? (
                        <>
                            {/* Progress indicator */}
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm text-muted-foreground">
                                    <span>
                                        Reviewing part {currentPartIndex + 1} of{" "}
                                        {partIds.length}
                                    </span>
                                    <span>
                                        {Math.round(progress)}% complete
                                    </span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>

                            <Separator />

                            <div>
                                {/* Current part being reviewed */}
                                <H2>{currentPartData.part.name}</H2>
                                {currentPartData.part.description && (
                                    <P className="text-sm">
                                        {currentPartData.part.description}
                                    </P>
                                )}
                                {currentPartData.part.recap && (
                                    <P className="text-sm text-muted-foreground">
                                        {currentPartData.part.recap}
                                    </P>
                                )}
                            </div>

                            <Separator />

                            {/* All matches for this part */}
                            <div className="space-y-2">
                                <P className="font-semibold">
                                    Potential Matches (
                                    {currentPartData.matches.length})
                                </P>
                                <ScrollArea className="h-[250px] pr-4">
                                    <div className="space-y-3">
                                        {currentPartData.matches.map(
                                            (match, index) => {
                                                const isSelected =
                                                    selectedMatches.get(
                                                        match.newPartInteraction
                                                            .id,
                                                    ) === match.existingPart.id;
                                                const cardId = `${match.existingPart.id}-${index}`;
                                                const isExpanded =
                                                    expandedCards.has(cardId);
                                                const latestInteraction =
                                                    match.existingPart
                                                        .latestInteraction;

                                                return (
                                                    <Card
                                                        key={`${match.newPartInteraction.id}-${match.existingPart.id}-${index}`}
                                                        className={cn(
                                                            isSelected &&
                                                                "border-primary bg-primary/10",
                                                            shouldDeemphasize(
                                                                match.confidence,
                                                            ) && "opacity-70",
                                                            "mt-1",
                                                        )}
                                                    >
                                                        <CardHeader
                                                            className="pb-3 cursor-pointer"
                                                            onClick={() => {
                                                                const newExpanded =
                                                                    new Set(
                                                                        expandedCards,
                                                                    );
                                                                if (
                                                                    isExpanded
                                                                ) {
                                                                    newExpanded.delete(
                                                                        cardId,
                                                                    );
                                                                } else {
                                                                    newExpanded.add(
                                                                        cardId,
                                                                    );
                                                                }
                                                                setExpandedCards(
                                                                    newExpanded,
                                                                );
                                                            }}
                                                        >
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex-1 space-y-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-auto p-0"
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation();
                                                                                const newExpanded =
                                                                                    new Set(
                                                                                        expandedCards,
                                                                                    );
                                                                                if (
                                                                                    isExpanded
                                                                                ) {
                                                                                    newExpanded.delete(
                                                                                        cardId,
                                                                                    );
                                                                                } else {
                                                                                    newExpanded.add(
                                                                                        cardId,
                                                                                    );
                                                                                }
                                                                                setExpandedCards(
                                                                                    newExpanded,
                                                                                );
                                                                            }}
                                                                        >
                                                                            {isExpanded ? (
                                                                                <ChevronDown className="size-4" />
                                                                            ) : (
                                                                                <ChevronRight className="size-4" />
                                                                            )}
                                                                        </Button>
                                                                        <CardTitle className="text-base">
                                                                            {
                                                                                match
                                                                                    .existingPart
                                                                                    .name
                                                                            }
                                                                        </CardTitle>
                                                                        <Badge
                                                                            variant={getConfidenceBadgeVariant(
                                                                                match.confidence,
                                                                            )}
                                                                        >
                                                                            {getConfidenceLabel(
                                                                                match.confidence,
                                                                            )}
                                                                        </Badge>
                                                                    </div>
                                                                    {latestInteraction && (
                                                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                            <Calendar className="size-3" />
                                                                            <span>
                                                                                {format(
                                                                                    new Date(
                                                                                        latestInteraction.sessionDate,
                                                                                    ),
                                                                                    "MMM d, yyyy",
                                                                                )}
                                                                            </span>
                                                                            <span>
                                                                                •
                                                                            </span>
                                                                            <span>
                                                                                {
                                                                                    latestInteraction.sessionTitle
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div className="space-y-2">
                                                                        <div className="flex items-start gap-2">
                                                                            <ArrowUp className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                                            <P className="text-sm text-muted-foreground">
                                                                                {
                                                                                    match.reasoning
                                                                                }
                                                                            </P>
                                                                        </div>
                                                                        {match.differences && (
                                                                            <div className="flex items-start gap-2">
                                                                                <ArrowDown className="size-4 text-brand-terra-600 mt-0.5 flex-shrink-0" />
                                                                                <P className="text-sm text-muted-foreground">
                                                                                    {
                                                                                        match.differences
                                                                                    }
                                                                                </P>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <Button
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleLinkToMatch(
                                                                            match
                                                                                .newPartInteraction
                                                                                .id,
                                                                            match
                                                                                .existingPart
                                                                                .id,
                                                                        );
                                                                    }}
                                                                    variant={
                                                                        isSelected
                                                                            ? "default"
                                                                            : "outline"
                                                                    }
                                                                    size="sm"
                                                                    className="gap-2"
                                                                >
                                                                    {isSelected ? (
                                                                        <>
                                                                            <Check className="size-4" />
                                                                            Linked
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Link2 className="size-4" />
                                                                            Link
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </CardHeader>
                                                        {isExpanded &&
                                                            latestInteraction && (
                                                                <CardContent className="pt-0">
                                                                    <div className="space-y-2">
                                                                        {match
                                                                            .existingPart
                                                                            .description && (
                                                                            <div>
                                                                                <P className="text-xs font-medium text-muted-foreground mb-1">
                                                                                    Description
                                                                                </P>
                                                                                <P className="text-sm">
                                                                                    {
                                                                                        match
                                                                                            .existingPart
                                                                                            .description
                                                                                    }
                                                                                </P>
                                                                            </div>
                                                                        )}
                                                                        <Separator className="my-2" />
                                                                        <P className="text-xs font-medium text-muted-foreground">
                                                                            Attributes
                                                                            from{" "}
                                                                            {
                                                                                latestInteraction.sessionTitle
                                                                            }
                                                                        </P>
                                                                        {ATTRIBUTE_LIST.map(
                                                                            (
                                                                                attr,
                                                                            ) => {
                                                                                // Access attributes from the attributes array of objects
                                                                                const attributesArray =
                                                                                    latestInteraction.attributes ||
                                                                                    [];
                                                                                const attributeData =
                                                                                    attributesArray.find(
                                                                                        (
                                                                                            a,
                                                                                        ) =>
                                                                                            a.name ===
                                                                                                attr.key ||
                                                                                            attr.alternateKeys?.includes(
                                                                                                a.name,
                                                                                            ),
                                                                                    );
                                                                                const value =
                                                                                    attributeData?.content;

                                                                                if (
                                                                                    !value ||
                                                                                    value ===
                                                                                        "Unclear"
                                                                                )
                                                                                    return null;

                                                                                return (
                                                                                    <div
                                                                                        key={
                                                                                            attr.key
                                                                                        }
                                                                                    >
                                                                                        <P className="text-xs font-medium text-muted-foreground mb-1">
                                                                                            {
                                                                                                attr.label
                                                                                            }
                                                                                        </P>
                                                                                        <P className="text-sm">
                                                                                            {
                                                                                                value
                                                                                            }
                                                                                        </P>
                                                                                    </div>
                                                                                );
                                                                            },
                                                                        )}
                                                                    </div>
                                                                </CardContent>
                                                            )}
                                                    </Card>
                                                );
                                            },
                                        )}
                                    </div>
                                </ScrollArea>

                                {/* Search for existing parts */}
                                <div className="space-y-2 mt-4">
                                    <P className="text-sm font-medium text-muted-foreground">
                                        Or search for an existing part:
                                    </P>
                                    <div
                                        className="relative"
                                        data-search-container
                                    >
                                        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            value={searchQuery}
                                            onChange={(e) =>
                                                setSearchQuery(e.target.value)
                                            }
                                            onFocus={() =>
                                                setShowSearchPopover(true)
                                            }
                                            placeholder="Search parts by name..."
                                            className="pl-9"
                                        />
                                        {showSearchPopover &&
                                            searchQuery.length >= 2 && (
                                                <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover p-1 shadow-md">
                                                    {isSearching ? (
                                                        <div className="px-3 py-2 text-sm text-muted-foreground">
                                                            Searching...
                                                        </div>
                                                    ) : searchResults.length >
                                                      0 ? (
                                                        <div className="max-h-[200px] overflow-y-auto">
                                                            {searchResults.map(
                                                                (part) => {
                                                                    const isSelected =
                                                                        selectedMatches.get(
                                                                            currentPartData
                                                                                ?.part
                                                                                .id ||
                                                                                "",
                                                                        ) ===
                                                                        part.id;
                                                                    return (
                                                                        <button
                                                                            key={
                                                                                part.id
                                                                            }
                                                                            onClick={() => {
                                                                                if (
                                                                                    currentPartData
                                                                                ) {
                                                                                    handleLinkToMatch(
                                                                                        currentPartData
                                                                                            .part
                                                                                            .id,
                                                                                        part.id,
                                                                                    );
                                                                                    setSearchQuery(
                                                                                        "",
                                                                                    );
                                                                                    setShowSearchPopover(
                                                                                        false,
                                                                                    );
                                                                                }
                                                                            }}
                                                                            className={`flex w-full items-center justify-between rounded-sm px-3 py-2 text-sm hover:bg-accent ${
                                                                                isSelected
                                                                                    ? "bg-accent"
                                                                                    : ""
                                                                            }`}
                                                                        >
                                                                            <div className="flex-1 text-left">
                                                                                <div className="font-medium">
                                                                                    {
                                                                                        part.name
                                                                                    }
                                                                                </div>
                                                                                {part.latestInteraction && (
                                                                                    <div className="text-xs text-muted-foreground">
                                                                                        {format(
                                                                                            new Date(
                                                                                                part.latestInteraction.sessionDate,
                                                                                            ),
                                                                                            "MMM d, yyyy",
                                                                                        )}{" "}
                                                                                        •{" "}
                                                                                        {
                                                                                            part
                                                                                                .latestInteraction
                                                                                                .sessionTitle
                                                                                        }
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            {isSelected && (
                                                                                <Check className="size-4 text-primary" />
                                                                            )}
                                                                        </button>
                                                                    );
                                                                },
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="px-3 py-2 text-sm text-muted-foreground">
                                                            No parts found
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                </div>

                                {/* Action buttons below matches */}
                                <div className="flex justify-end gap-2 mt-2">
                                    <Button
                                        onClick={handleSkipPart}
                                        variant="ghost"
                                        size="sm"
                                        className="gap-2"
                                    >
                                        <SkipForward className="size-4" />
                                        I'm not sure
                                    </Button>
                                    <Button
                                        onClick={handleSkipPart}
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                    >
                                        This is a new part
                                    </Button>
                                </div>
                            </div>

                            <Separator />

                            <DialogFooter className="flex justify-end pt-2">
                                <Button
                                    onClick={handleSkipPart}
                                    variant={
                                        currentPartIndex === partIds.length - 1
                                            ? "default"
                                            : selectedMatches.has(currentPartId)
                                              ? "default"
                                              : "secondary"
                                    }
                                    className="gap-2"
                                >
                                    {currentPartIndex === partIds.length - 1 ? (
                                        <>
                                            <Check className="size-4" />
                                            Complete Review
                                        </>
                                    ) : (
                                        <>
                                            Next Part
                                            <SkipForward className="size-4" />
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : (
                        /* Completion state */
                        <div className="flex flex-col items-center justify-center gap-4 py-12">
                            <div className="rounded-full bg-primary/10 p-3">
                                <Check className="size-8 text-primary" />
                            </div>
                            <div className="space-y-2 text-center">
                                <P className="font-semibold">
                                    Review Complete!
                                </P>
                                <P className="text-sm text-muted-foreground">
                                    {acceptedMatches.length > 0
                                        ? `Linked ${
                                              acceptedMatches.length
                                          } part${
                                              acceptedMatches.length === 1
                                                  ? ""
                                                  : "s"
                                          } to existing entries`
                                        : "No parts were linked"}
                                </P>
                            </div>
                            <Button onClick={() => onOpenChange(false)}>
                                Close
                            </Button>
                        </div>
                    )}
                </PartSimilarityErrorBoundary>
            </DialogContent>
        </Dialog>
    );
}
